/* eslint-disable import/no-cycle */ // TODO: {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4218}
import { useRecoilValue } from 'recoil';

import log from 'common/ScalyrLogger';

import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';

import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { ErrorResult } from 'hooks/useLRQ/useLRQ';
import { DashboardChartType } from 'components/dashboards/types';
import {
  createEventsPageUrl,
  DashboardPlotData,
  DashboardPlotValuePair,
  getExploreHref,
  getPlots,
  getType,
} from 'components/dashboards/dashboardData';
import DashboardNumber, { isNumberGraph } from 'components/dashboards/DashboardPage/DashboardNumber';
import DashboardHighcharts from 'components/dashboards/DashboardPage/DashboardHighcharts';
import NoResults from 'components/dashboards/DashboardPage/NoResults';
import DashboardIncompleteResults from 'components/dashboards/DashboardPage/DashboardIncompleteResults';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import { getPieChartValueColumn } from 'components/search/PowerQueriesPage/components/PowerQueryCharts/PowerQueryChartResults';

import { getLrqStatus, getPieSeriesData, LRQ_STATUS } from 'common/util/graph';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import { canBeRenderedAsPieChart, getPQNumericValue } from 'common/util/pqUtil';

import { plotsStylesState } from 'styles/stylesProvider';
import { ErrorEditGraphJSON } from './modals/EditGraphJSON';

/**
 * Crack open an LRQ Plot ErrorResult to obtain the error within.
 *
 * todo: suss out any other possible error syntaxes
 *
 * @param errorResult A potential LRQ error
 */
export const getRequestErrorMessage = (errorResult: ErrorResult): string => {
  if (errorResult.terminationReasons !== undefined) {
    const reasons = errorResult.terminationReasons as Array<string>;
    if (reasons.length > 0) {
      return reasons[0];
    }
  }
  return 'An unknown error has occurred.';
};

/**
 * Line, bar or area chart graph display for a dashboard.
 *
 * @prop config                 The config stanza for just this chart
 * @prop dataIds                The list of LRQ data ids to retreive data (only one for a power query)
 * @prop chartInstanceCallback  A callback to capture the Highcharts chart instance -- used for reszing
 */
const DashboardChart = ({
  index,
  config,
  dataIds,
  params,
  windowWidth,
  chartInstanceCallback,
}: DashboardChartType): JSX.Element => {

  const { results, errors } = useEventCache(dataIds);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const vizType = getType(config);
  const plotsStyles = useRecoilValue(plotsStylesState);

  const exploreHref = getExploreHref(config, params, startTime, endTime);

  if (!dataIds || dataIds.length === 0) {
    return <></>;
  }

  const lrqStatus = getLrqStatus(results, errors, vizType.isTimeseries);
  if (lrqStatus === LRQ_STATUS.IN_PROGRESS) {
    return (<LoadingPlaceholder offsetTop={0} />);
  }

  if (lrqStatus === LRQ_STATUS.HAS_ERRORS) {
    const message = getRequestErrorMessage(errors.find((error) => error) as ErrorResult);
    const isSyntaxError = message.toLowerCase().includes('parsing');
    if (isSyntaxError) {
      return (<ErrorEditGraphJSON message={message} graphIndex={index} graphConfig={config} />);
    }
    return (<ErrorPlaceholder error={{ message }} />);
  }

  if (lrqStatus === LRQ_STATUS.NO_RESULTS) { // Not sure exactly what this case would be
    log.error(`Dashboard LRQ status of no results for: ${JSON.stringify(config)} dashboard config.`);
    return (<div className="DashboardGraph__NoResults DashboardGraph__NoResults--Error">No results returned, please try again.</div>);
  }

  if (lrqStatus === LRQ_STATUS.NO_MATCHES) {
    return <NoResults config={config} params={params} />;
  }

  let warning;
  let plots: DashboardPlotData[] = [];
  const matchCount = results[0]?.pqResultData?.matchCount;
  const omittedEvents = results[0]?.pqResultData?.omittedEvents;
  const partialResultsDueToTimeLimit = results[0]?.pqResultData?.partialResultsDueToTimeLimit;

  // --------------------------------------------------------------------------------------------------------------- PIE / DONUT DISPLAY
  if (vizType.isPie && results[0].pqResultData && (results[0].pqResultData.cells || results[0].pqResultData.values)) {
    const pqResultData = results[0].pqResultData;
    const columns = pqResultData.columns || [];
    const rowValues = [...(pqResultData.cells || pqResultData.values)];

    const labelCol = 0;
    const valueCol = getPieChartValueColumn(columns);

    if (columns.length < 1 || columns[valueCol].format !== 'number') {
      return <NoResults config={config} params={params} message="Please make sure your query returns at least one numeric column." />;
    }

    // Gather the data and calc totals and percentages for the pie
    const valuesColumn = rowValues.map((rowValue) => getPQNumericValue(rowValue[valueCol]));
    if (!canBeRenderedAsPieChart(valuesColumn)) {
      return <NoResults config={config} params={params} />;
    }

    const seriesData = getPieSeriesData(rowValues, valueCol, labelCol, config.maxPieSlices);

    plots = [{ facet: columns[labelCol].name, label: columns[valueCol].name, plotStyle: 'pie', seriesData }];
  } else {
    plots = getPlots(config, results, plotsStyles, params, true);
  }

  if (isNumberGraph(config)) {
    if (plots.length && plots[0].seriesData?.length) {
      const data = plots[0].seriesData[0] as DashboardPlotValuePair;
      const numberValue = data[0];
      const linkValue = createEventsPageUrl(startTime, endTime, config, plots, 0, true);
      return (<DashboardNumber config={config} linkValue={linkValue} numberValue={numberValue} />);
    }
    return (<div className="DashboardGraph__NoResults DashboardGraph__NoResults--Error">Unable to find a number to display.</div>);
  }

  if (config.shift && plots[0]?.seriesData && plots[1]?.seriesData) {
    const shiftedPlot = plots[1];
    shiftedPlot.seriesData = (plots[0].seriesData as DashboardPlotValuePair[])
      .map((point, i) => {
        const point2 = (plots[1].seriesData || [])[i] as DashboardPlotValuePair;
        return [point[0], point2[1]];
      }) as DashboardPlotValuePair[];
    plots[1] = shiftedPlot;
    const deltaPlot = {
      ...plots[0],
      label: 'delta',
      color: undefined,
    };
    deltaPlot.seriesData = (plots[0].seriesData as DashboardPlotValuePair[])
      .map((point, i) => {
        const point2 = (plots[1].seriesData || [])[i] as DashboardPlotValuePair;
        return [point[0], point[1] - point2[1]];
      }) as DashboardPlotValuePair[];
    plots.push(deltaPlot);
  }

  return (
    <>
      <DashboardIncompleteResults
        matchCount={matchCount}
        omittedEvents={omittedEvents}
        partialResultsDueToTimeLimit={partialResultsDueToTimeLimit}
      />
      <DashboardHighcharts
        index={index}
        config={config}
        warning={warning}
        plots={plots}
        windowWidth={windowWidth}
        chartInstanceCallback={chartInstanceCallback}
        exploreHref={exploreHref}
      />
    </>
  );

};

export default DashboardChart;
